<template>
  <el-dropdown trigger="click" placement="bottom-start">
    <button
      type="button"
      class="relative shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
      :title="$t('chat.status.self.' + selfStatus)"
      :aria-label="$t('chat.status.change')"
    >
      <img
        :src="avatarUrl || '/images/default-avatar.svg'"
        alt=""
        class="w-9 h-9 rounded-full object-cover"
      />
      <!-- Status dot overlay on the bottom-right of the avatar -->
      <span
        class="absolute bottom-0 right-0 w-3 h-3 rounded-full ring-2 ring-[var(--surface-color,#0d0e12)]"
        :class="dotClass(selfStatus)"
      ></span>
    </button>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="opt in options"
          :key="opt.value"
          @click="pick(opt.value)"
        >
          <span class="flex items-center gap-2 min-w-[120px]">
            <span class="w-2.5 h-2.5 rounded-full" :class="dotClass(opt.value)"></span>
            <span class="flex-1">{{ $t('chat.status.self.' + opt.value) }}</span>
            <el-icon v-if="selfStatus === opt.value" size="12"><Check /></el-icon>
          </span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup>
import { computed } from 'vue'
import { Check } from '@element-plus/icons-vue'
import { usePresence } from '~/composables/chat/usePresence'

/**
 * Self-status picker. Renders the user's avatar with a colored dot overlay
 * reflecting the current status; clicking opens a dropdown to change it to
 * Online / AFK / DND. The change is optimistic — `setSelfStatus` updates
 * local state immediately and reverts on server failure.
 */

const { user } = useUserSession()
const { selfStatus, setSelfStatus } = usePresence()

const options = [
  { value: 'online' },
  { value: 'afk' },
  { value: 'dnd' }
]

const avatarUrl = computed(() => {
  const raw = user.value?.chatProfile?.avatarUrl
    || user.value?.avatar
    || user.value?.spotify?.imageUrl
    || user.value?.soundcloud?.imageUrl
    || null
  // S3 avatars live in a private bucket — route through the asset proxy
  // or the <img alt=""> gets a 403. External CDN URLs go through the proxy too
  // for consistent caching / tenant scoping.
  if (!raw || typeof raw !== 'string') return null
  if (raw.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(raw)}`
  return raw
})

/** Tailwind utility class for the status dot colour. */
function dotClass(status) {
  if (status === 'afk') return 'bg-amber-400'
  if (status === 'dnd') return 'bg-red-500'
  return 'bg-emerald-400' // online
}

async function pick(status) {
  if (selfStatus.value === status) return
  await setSelfStatus(status)
}
</script>
